import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldQuestion,
  FileText,
  Camera,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  Loader2,
  ExternalLink,
  History,
  Lock,
  Unlock,
  DollarSign,
  TrendingUp,
  Globe,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react'
import { KycAPI } from '../services/api'

// Status configurations
const statusConfig = {
  pending: {
    icon: ShieldQuestion,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30',
    title: 'Verification Pending',
    description: 'Your identity has not been verified yet. Complete the verification process to unlock all features.',
  },
  in_review: {
    icon: Clock,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30',
    title: 'Under Review',
    description: 'Your verification is being reviewed by our team. This process may take up to 24 hours.',
  },
  approved: {
    icon: ShieldCheck,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/30',
    title: 'Verification Approved',
    description: 'Your identity has been successfully verified. You have full access to all features.',
  },
  rejected: {
    icon: ShieldAlert,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30',
    title: 'Verification Rejected',
    description: 'Your verification was rejected. You can try again with valid documents.',
  },
}

// Session status badge
const SessionStatusBadge = ({ status }) => {
  const statusStyles = {
    not_started: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Not Started' },
    in_progress: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'In Progress' },
    approved: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Approved' },
    declined: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Declined' },
    in_review: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'In Review' },
    abandoned: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Abandoned' },
    expired: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Expired' },
  }
  const style = statusStyles[status] || statusStyles.not_started
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  )
}

export default function KycPage() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [kycStatus, setKycStatus] = useState(null)
  const [limits, setLimits] = useState(null)
  const [activeSession, setActiveSession] = useState(null)
  const [sessionHistory, setSessionHistory] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const [creatingSession, setCreatingSession] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  // Fetch KYC status
  const fetchKycStatus = useCallback(async () => {
    try {
      setError(null)
      const response = await KycAPI.getStatus()
      if (response.success) {
        setKycStatus(response.data)
        setActiveSession(response.data.activeSession)
        setLimits(response.data.limits)
      }
    } catch (err) {
      setError(err.message || 'Error loading KYC status')
    }
  }, [])

  // Fetch session history
  const fetchSessionHistory = useCallback(async () => {
    try {
      const response = await KycAPI.getSessions({ limit: 10 })
      if (response.success) {
        setSessionHistory(response.data.sessions || [])
      }
    } catch (err) {
      console.error('Error fetching session history:', err)
    }
  }, [])

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchKycStatus(), fetchSessionHistory()])
      setLoading(false)
    }
    loadData()
  }, [fetchKycStatus, fetchSessionHistory])

  // Start verification
  const startVerification = async () => {
    setCreatingSession(true)
    setError(null)
    try {
      const response = await KycAPI.createSession()
      if (response.success) {
        // Open verification URL in new tab
        if (response.data.verificationUrl) {
          window.open(response.data.verificationUrl, '_blank')
        }
        // Refresh status
        await fetchKycStatus()
        await fetchSessionHistory()
      }
    } catch (err) {
      setError(err.message || 'Error starting verification')
    } finally {
      setCreatingSession(false)
    }
  }

  // Refresh session status
  const refreshSession = async () => {
    if (!activeSession?.id) return
    setRefreshing(true)
    try {
      await KycAPI.refreshSession(activeSession.id)
      await fetchKycStatus()
      await fetchSessionHistory()
    } catch (err) {
      setError(err.message || 'Error updating status')
    } finally {
      setRefreshing(false)
    }
  }

  // Continue existing session
  const continueSession = () => {
    if (activeSession?.verificationUrl || activeSession?.didit_session_url) {
      window.open(activeSession.verificationUrl || activeSession.didit_session_url, '_blank')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading verification information...</p>
        </div>
      </div>
    )
  }

  const config = statusConfig[kycStatus?.status] || statusConfig.pending
  const StatusIcon = config.icon

  return (
    <div className="min-h-screen bg-[#0a0a0f] pb-20">
      {/* Header */}
      <div className="bg-gradient-to-b from-indigo-900/30 to-transparent px-4 pt-8 pb-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Shield className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Identity Verification (KYC)</h1>
            <p className="text-gray-400 max-w-lg mx-auto">
              Identity verification is required by banking regulations to protect your account and prevent fraud.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-6">
        {/* Error Alert */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-300"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`rounded-2xl border ${config.borderColor} ${config.bgColor} p-6 mb-6`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${config.bgColor}`}>
              <StatusIcon className={`w-8 h-8 ${config.color}`} />
            </div>
            <div className="flex-1">
              <h2 className={`text-xl font-semibold ${config.color} mb-1`}>{config.title}</h2>
              <p className="text-gray-400 text-sm mb-4">{config.description}</p>

              {/* Additional Info */}
              {kycStatus?.status === 'approved' && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Verified on:</span>
                    <p className="text-white">
                      {kycStatus.verifiedAt
                        ? new Date(kycStatus.verifiedAt).toLocaleDateString('en-US')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Document:</span>
                    <p className="text-white">{kycStatus.documentType || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Country:</span>
                    <p className="text-white">{kycStatus.country || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Verified name:</span>
                    <p className="text-white">{kycStatus.fullName || '-'}</p>
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              {kycStatus?.status === 'rejected' && kycStatus?.rejectionReason && (
                <div className="mt-4 p-3 bg-red-500/10 rounded-lg">
                  <p className="text-red-400 text-sm">
                    <strong>Reason:</strong> {kycStatus.rejectionReason}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-wrap gap-3">
            {/* Not verified - Start verification */}
            {(kycStatus?.status === 'pending' || kycStatus?.status === 'rejected') && !activeSession && (
              <button
                onClick={startVerification}
                disabled={creatingSession}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                {creatingSession ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Camera className="w-5 h-5" />
                    Start Verification
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            )}

            {/* Has active session - Continue */}
            {activeSession && ['not_started', 'in_progress'].includes(activeSession.status) && (
              <>
                <button
                  onClick={continueSession}
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                  Continue Verification
                </button>
                <button
                  onClick={refreshSession}
                  disabled={refreshing}
                  className="flex items-center gap-2 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh Status
                </button>
              </>
            )}

            {/* In review - just refresh button */}
            {kycStatus?.status === 'in_review' && (
              <button
                onClick={fetchKycStatus}
                className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                Check Status
              </button>
            )}
          </div>
        </motion.div>

        {/* Limits Card */}
        {limits && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Lock className="w-5 h-5 text-indigo-400" />
                Account Limits
              </h3>
              {kycStatus?.status === 'approved' ? (
                <span className="flex items-center gap-1 text-emerald-400 text-sm">
                  <Unlock className="w-4 h-4" />
                  Unlocked
                </span>
              ) : (
                <span className="flex items-center gap-1 text-yellow-400 text-sm">
                  <Lock className="w-4 h-4" />
                  Limited
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-900/50 rounded-xl p-4">
                <DollarSign className="w-6 h-6 text-indigo-400 mb-2" />
                <p className="text-gray-400 text-xs mb-1">Daily Transfer</p>
                <p className="text-white font-semibold">
                  ${limits.daily_transfer_limit?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4">
                <TrendingUp className="w-6 h-6 text-emerald-400 mb-2" />
                <p className="text-gray-400 text-xs mb-1">Monthly Transfer</p>
                <p className="text-white font-semibold">
                  ${limits.monthly_transfer_limit?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4">
                <CreditCard className="w-6 h-6 text-yellow-400 mb-2" />
                <p className="text-gray-400 text-xs mb-1">Daily Withdrawal</p>
                <p className="text-white font-semibold">
                  ${limits.daily_withdrawal_limit?.toLocaleString() || '0'}
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4">
                <Globe className="w-6 h-6 text-blue-400 mb-2" />
                <p className="text-gray-400 text-xs mb-1">Max Transaction</p>
                <p className="text-white font-semibold">
                  ${limits.max_single_transaction?.toLocaleString() || '0'}
                </p>
              </div>
            </div>

            {/* Features */}
            <div className="mt-4 pt-4 border-t border-slate-700/50">
              <p className="text-gray-400 text-sm mb-3">Available features:</p>
              <div className="flex flex-wrap gap-3">
                <FeatureBadge
                  enabled={limits.can_invest}
                  label="Investments"
                />
                <FeatureBadge
                  enabled={limits.can_trade_crypto}
                  label="Crypto Trading"
                />
                <FeatureBadge
                  enabled={limits.can_apply_loans}
                  label="Loans"
                />
                <FeatureBadge
                  enabled={limits.can_international_transfer}
                  label="International Transfers"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Verification Steps */}
        {(kycStatus?.status === 'pending' || kycStatus?.status === 'rejected') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6 mb-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-400" />
              Verification Process
            </h3>
            <div className="space-y-4">
              <VerificationStep
                number={1}
                title="Prepare your document"
                description="Have your ID document ready (National ID, Passport, or Driver's License)"
              />
              <VerificationStep
                number={2}
                title="Take a photo of the document"
                description="Photograph the front and back of your document in a well-lit area"
              />
              <VerificationStep
                number={3}
                title="Facial verification"
                description="We will take a selfie to confirm that you are the one performing the verification"
              />
              <VerificationStep
                number={4}
                title="Automatic review"
                description="Our system will verify the authenticity of the documents within minutes"
              />
            </div>

            <div className="mt-6 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-300">
                  <p className="font-medium text-indigo-400 mb-1">Accepted documents:</p>
                  <ul className="list-disc list-inside text-gray-400 space-y-1">
                    <li>National ID Card</li>
                    <li>Valid Passport</li>
                    <li>Driver's License</li>
                    <li>Government-issued ID (other countries)</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Session History */}
        {sessionHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden"
          >
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
            >
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-400" />
                Verification History ({sessionHistory.length})
              </h3>
              {showHistory ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            <AnimatePresence>
              {showHistory && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-slate-700/50"
                >
                  <div className="p-4 space-y-3">
                    {sessionHistory.map((session) => (
                      <div
                        key={session.id}
                        className="bg-slate-900/50 rounded-xl p-4 flex items-center justify-between"
                      >
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <SessionStatusBadge status={session.status} />
                            {session.document_type && (
                              <span className="text-gray-400 text-xs">
                                {session.document_type}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm">
                            {new Date(session.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          {session.full_name && (
                            <p className="text-white text-sm">{session.full_name}</p>
                          )}
                          {session.document_country && (
                            <p className="text-gray-500 text-xs">{session.document_country}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  )
}

// Feature Badge Component
const FeatureBadge = ({ enabled, label }) => (
  <div
    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
      enabled
        ? 'bg-emerald-500/20 text-emerald-400'
        : 'bg-slate-700/50 text-gray-500'
    }`}
  >
    {enabled ? (
      <CheckCircle className="w-4 h-4" />
    ) : (
      <Lock className="w-4 h-4" />
    )}
    {label}
  </div>
)

// Verification Step Component
const VerificationStep = ({ number, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-medium">
      {number}
    </div>
    <div>
      <h4 className="text-white font-medium mb-1">{title}</h4>
      <p className="text-gray-400 text-sm">{description}</p>
    </div>
  </div>
)
